import { NgFor } from '@angular/common';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfiguracoesOperacao, EntidadeConfiguracao, ExibicaoConfiguracao, ExibicoesConfiguracoes, TipoConfiguracaoConnect } from 'app/interfaces';
import { ConfiguracoesSyncService, ConfiguracoesService, ComunicadorService, DialogAgviewConnectService } from 'app/services';
import { filter, switchMap, take } from 'rxjs';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-configuracao-operacao',
  standalone: true,
  imports: [NgFor, FormsModule, TranslocoModule],
  templateUrl: './configuracao-operacao.component.html',
  styleUrl: './configuracao-operacao.component.css',
})

export class ConfiguracaoOperacaoComponent implements OnInit {
  @Output() trocarTela: EventEmitter<string> = new EventEmitter<string>();

  public dialogVisivel: boolean = false;
  public readonly NOME_ENTIDADE_CONFIGURACAO_OPERACAO: EntidadeConfiguracao = 'OperationConfigEntity';
  public readonly NOME_CONFIGURACAO_ATIVACAO_AGITADOR_AUTOMATICO: string = 'isSutomaticAgitatorActive';
  public readonly NOME_CONFIGURACAO_TEMPO_PARA_INICIAR_AGITADOR_AUTOMATICO: string = 'timeStartAutomaticaAgitator';
  public readonly NOME_CONFIGURACAO_TEMPO_DE_ESPERA_PARA_DESVIO_DE_FLUXO: string = 'flowDiversionWaitingTime';
  public semprePresentesObjetoEnvio = ['flowDiversionWaitingTime', 'timeStartAutomaticaAgitator'];
  public itens: ExibicoesConfiguracoes = [];
  public arrayItensFiltrados: ExibicoesConfiguracoes = [];
  public configuracoesCadastradas!: TipoConfiguracaoConnect;
  public imei: string = '';
  private readonly TRANSLATION_PREFIX = 'COMPONENTS.AGVIEW_CONNECT.CONFIG_OPERACAO.ITEMS';

  constructor(
    private dialogAgviewConnectService: DialogAgviewConnectService,
    private comunicadorService: ComunicadorService,
    private configuracoesService: ConfiguracoesService,
    private configuracoesSyncService: ConfiguracoesSyncService,
    private translocoService: TranslocoService
  ) {
    this.inicializarItens();
  }

  ngOnInit(): void {
    this.carregarConfiguracoes();
    this.atribuirValoresConfiguracoes();
    this.assinarAtualizacaoConfiguracoes();
    this.observarMudancasWebsocket();
    this.assinarMudancaIdioma();
  }

  private traduzirItem(nomeConfiguracao: string): { titulo: string; subtitulo: string } {
    return {
      titulo: this.translocoService.translate(`${this.TRANSLATION_PREFIX}.${nomeConfiguracao}.titulo`),
      subtitulo: this.translocoService.translate(`${this.TRANSLATION_PREFIX}.${nomeConfiguracao}.subtitulo`),
    };
  }

  private traduzirItemTanque(index: number): { titulo: string; subtitulo: string } {
    const tankPrefix = 'COMPONENTS.AGVIEW_CONNECT.CONFIG_OPERACAO.TANK_ITEM';
    return {
      titulo: this.translocoService.translate(`${tankPrefix}.titulo`, { index }),
      subtitulo: this.translocoService.translate(`${tankPrefix}.subtitulo`, { index }),
    };
  }

  private inicializarItens(): void {
    const staticItems: Array<Omit<ExibicaoConfiguracao, 'titulo' | 'subtitulo'> & { nomeConfiguracao: string }> = [
      { nomeConfiguracao: 'connectCanLicense', deveSerMostrado: true, isToggle: true, valor: false, isSubconfiguracao: false },
      { nomeConfiguracao: 'implementFleet', deveSerMostrado: true, isToggle: false, valor: 0, isSubconfiguracao: false },
      { nomeConfiguracao: 'isFlowAndTemperatureActive', deveSerMostrado: true, isToggle: true, valor: false, isSubconfiguracao: false },
      { nomeConfiguracao: 'solidLevelSensor', deveSerMostrado: true, isToggle: true, valor: false, isSubconfiguracao: false },
      { nomeConfiguracao: 'rampRetreatSensor', deveSerMostrado: true, isToggle: true, valor: false, isSubconfiguracao: false },
      { nomeConfiguracao: 'disarmSensor', deveSerMostrado: true, isToggle: true, valor: false, isSubconfiguracao: false },
      { nomeConfiguracao: 'startTimeOperation', deveSerMostrado: true, isToggle: false, unidadeMedida: 's', valor: 0, isSubconfiguracao: false },
      { nomeConfiguracao: 'timeUntilAutomaticStopReason', deveSerMostrado: true, isToggle: false, unidadeMedida: 's', valor: 0, isSubconfiguracao: false },
      { nomeConfiguracao: 'stripWidth', deveSerMostrado: true, isToggle: false, unidadeMedida: 'cm', valor: 0, isSubconfiguracao: false },
      { nomeConfiguracao: 'lineSpacing', deveSerMostrado: true, isToggle: false, unidadeMedida: 'cm', valor: 0, isSubconfiguracao: false },
      { nomeConfiguracao: 'numberLines', deveSerMostrado: true, isToggle: false, valor: 0, isSubconfiguracao: false },
      { nomeConfiguracao: 'sensorSVA', deveSerMostrado: true, isToggle: true, valor: false, isSubconfiguracao: false },
      { nomeConfiguracao: 'maxVelocity', deveSerMostrado: true, isToggle: false, unidadeMedida: 'km/h', valor: 0, isSubconfiguracao: false },
      { nomeConfiguracao: 'minVelocity', deveSerMostrado: true, isToggle: false, unidadeMedida: 'km/h', valor: 0, isSubconfiguracao: false },
      { nomeConfiguracao: this.NOME_CONFIGURACAO_ATIVACAO_AGITADOR_AUTOMATICO, deveSerMostrado: true, isToggle: true, valor: false, isSubconfiguracao: false },
      { nomeConfiguracao: this.NOME_CONFIGURACAO_TEMPO_PARA_INICIAR_AGITADOR_AUTOMATICO, isToggle: false, deveSerMostrado: false, unidadeMedida: 's', valor: 0, isSubconfiguracao: true },
      { nomeConfiguracao: this.NOME_CONFIGURACAO_TEMPO_DE_ESPERA_PARA_DESVIO_DE_FLUXO, isToggle: false, deveSerMostrado: false, unidadeMedida: 's', valor: 0, isSubconfiguracao: true },
    ];

    this.itens = staticItems.map(item => ({
      ...item,
      ...this.traduzirItem(item.nomeConfiguracao),
    }));

    this.arrayItensFiltrados = this.itens;
  }

  private atualizarTraducoes(): void {
    this.itens.forEach(item => {
      if (item.nomeConfiguracao.startsWith('currentTanque')) {
        const index = parseInt(item.nomeConfiguracao.replace('currentTanque', ''), 10);
        const traducao = this.traduzirItemTanque(index);
        item.titulo = traducao.titulo;
        item.subtitulo = traducao.subtitulo;
      } else {
        const traducao = this.traduzirItem(item.nomeConfiguracao);
        item.titulo = traducao.titulo;
        item.subtitulo = traducao.subtitulo;
      }
    });
  }

  private assinarMudancaIdioma(): void {
    this.translocoService.langChanges$.pipe(
      switchMap(lang => this.translocoService.selectTranslation(lang))
    ).subscribe(() => this.atualizarTraducoes());
  }

  public carregarConfiguracoes(): void {
    this.imei = this.configuracoesService.obterImei();
    this.configuracoesCadastradas = this.configuracoesService.obterPorEntidade('OperationConfigEntity');
    const licenca = this.configuracoesService.obterPorEntidade('License');

    const licenseString = typeof licenca === 'string' ? licenca : licenca?.licenseCode || '';

    if (licenseString) {
      const qtdTanques = this.obterQuantidadeTanques(licenseString);
      this.itens.push(...this.gerarItensTanques(qtdTanques));
    }
    this.atualizarValoresTanques();
  }

  public atribuirValoresConfiguracoes(): void {
    Object.entries(this.configuracoesCadastradas || {}).forEach(([nome, valor]) => {
      const item = this.itens.find(i => i.nomeConfiguracao === nome);
      if (item) {
        item.valor = valor;
        this.mostrarItensOcultos(item);
      }
    });

    this.atualizarValoresTanques();
  }

  public irParaTelaConfigGerais(): void {
    this.verificarAlteracoesEAtualizar();
    this.trocarTela.emit('configuracoes');
  }

  public verificarAlteracoesEAtualizar(): void {
    const configuracoesTela: Record<string, any> = this.obterConfiguracoesParaComparacao();
    const configuracoesOriginais: Record<string, any> = this.ordenarObjeto(this.configuracoesCadastradas);

    if (JSON.stringify(configuracoesTela) !== JSON.stringify(configuracoesOriginais)) {
      this.atualizarConfiguracoes();
    }
  }

  private obterConfiguracoesParaComparacao(): Record<string, any> {
    const valoresTanques: number[] = this.obterValoresTanquesParaSalvar();

    const configuracoesSemTanques = this.formatarConfiguracoesParaSalvar(
      this.itens.filter(i => !i.nomeConfiguracao.startsWith('currentTanque'))
    );

    const configuracoes = {
      ...configuracoesSemTanques,
      nominalCurrentTanks: valoresTanques,
    };

    return this.ordenarObjeto(configuracoes);
  }

  public atualizarConfiguracoes(): void {
    const novasConfiguracoes: Record<string, any> = this.formatarConfiguracoesParaSalvar(this.itens.filter(i => !i.nomeConfiguracao.startsWith('currentTanque')));
    novasConfiguracoes['nominalCurrentTanks'] = this.obterValoresTanquesParaSalvar();

    this.comunicadorService.atualizarConfiguracoes(this.imei, {
      [this.NOME_ENTIDADE_CONFIGURACAO_OPERACAO]: novasConfiguracoes,
    }).subscribe();

    this.configuracoesService.atualizarPorEntidade(this.NOME_ENTIDADE_CONFIGURACAO_OPERACAO, novasConfiguracoes);
    this.configuracoesSyncService.dispararNotificacaoEnvio();
  }

  private formatarConfiguracoesParaSalvar(itens: ExibicoesConfiguracoes): Record<string, any> {
    return itens.filter(i => i.deveSerMostrado || this.semprePresentesObjetoEnvio.includes(i.nomeConfiguracao)).reduce((acc, item) => {
      acc[item.nomeConfiguracao] = item.valor;
      return acc;
    }, {} as Record<string, any>);
  }

  public filtrarConfiguracoes(texto: string): void {
    const termo: string = this.limparString(texto);
    this.arrayItensFiltrados = this.itens.filter(item => this.limparString(item.titulo).includes(termo));
  }

  private limparString(texto: string): string {
    return texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  }

  public mostrarItensOcultos(item: ExibicaoConfiguracao): void {
    if (item.nomeConfiguracao !== this.NOME_CONFIGURACAO_ATIVACAO_AGITADOR_AUTOMATICO) return;

    this.exibirConfiguracaoPorNome(this.NOME_CONFIGURACAO_TEMPO_PARA_INICIAR_AGITADOR_AUTOMATICO, item.valor);
    this.exibirConfiguracaoPorNome(this.NOME_CONFIGURACAO_TEMPO_DE_ESPERA_PARA_DESVIO_DE_FLUXO, item.valor);

    this.arrayItensFiltrados
      .filter(p => p.nomeConfiguracao.startsWith('currentTanque'))
      .forEach(p => {
        p.deveSerMostrado = item.valor === true || item.valor === 1;
      });
  }

  private exibirConfiguracaoPorNome(nome: string, exibir: number | boolean | string): void {
    const item = this.arrayItensFiltrados.find(p => p.nomeConfiguracao === nome);
    if (item) item.deveSerMostrado = exibir == 1;
  }

  private gerarItensTanques(qtd: number): ExibicoesConfiguracoes {
    return Array.from({ length: qtd }, (_, i) => ({
      nomeConfiguracao: `currentTanque${i + 1}`,
      ...this.traduzirItemTanque(i + 1),
      isToggle: false,
      deveSerMostrado: false,
      unidadeMedida: 'mA',
      valor: 0,
      isSubconfiguracao: true,
    }));
  }

  private obterQuantidadeTanques(config: string): number {
    return config.split('|').slice(1).filter(p => p.startsWith('L-')).length;
  }

  private obterValoresTanquesParaSalvar(): number[] {
    const valores = this.itens
      .filter(i => i.nomeConfiguracao.startsWith('currentTanque'))
      .map(i => Number(i.valor))
      .filter(v => !isNaN(v) && v !== 0);
    return valores.length > 0 ? valores : [];
  }

  private ordenarObjeto(obj: Record<string, any>): Record<string, any> {
    return Object.keys(obj)
      .sort()
      .reduce((acc, key) => {
        acc[key] = obj[key];
        return acc;
      }, {} as Record<string, any>);
  }

  private assinarAtualizacaoConfiguracoes(): void {
    this.configuracoesSyncService.aoDispararAtualizacao()
      .pipe(take(1))
      .subscribe(() => this.verificarAlteracoesEAtualizar());
  }

  private observarMudancasWebsocket(): void {
    this.configuracoesService.obterPorEntidadeWebsocket(this.NOME_ENTIDADE_CONFIGURACAO_OPERACAO)
      .pipe(filter((config): config is ConfiguracoesOperacao => !!config))
      .subscribe(config => {
        this.configuracoesCadastradas = config;
        this.atribuirValoresConfiguracoes();
      });
  }

  public abrirDialogConfiguracaoParametro(parametro: any): void {
    const indiceParametroAlterado: number = this.itens.findIndex((item: any) => parametro == item);

    if (indiceParametroAlterado == -1) return;

    this.dialogAgviewConnectService.open(parametro).subscribe((novoValorParametro: number) => {
      this.itens[indiceParametroAlterado].valor = novoValorParametro;
    });
  }

  private isConfiguracoesOperacao(config: TipoConfiguracaoConnect): config is ConfiguracoesOperacao {
    return 'nominalCurrentTanks' in config;
  }

  private atualizarValoresTanques(): void {
    if (this.isConfiguracoesOperacao(this.configuracoesCadastradas)) {
      const nominalCurrentTanks = this.configuracoesCadastradas.nominalCurrentTanks;

      if (Array.isArray(nominalCurrentTanks)) {
        nominalCurrentTanks.forEach((valor, index) => {
          const item = this.itens.find(i => i.nomeConfiguracao === `currentTanque${index + 1}`);
          if (item) item.valor = valor;
        });
      }
    }
  }
}